"use client"

import { toast } from "sonner"
import { DEBUG, debugLog, debugGroup, debugGroupEnd, debugError } from '@/lib/debug'

/**
 * 🎯 ПРОСТОЙ API - ПРЯМЫЕ ЗАПРОСЫ
 * Только нужные функции, никаких дублирований
 */

// 🌐 Базовый URL API (основной бэкенд - всегда продакшн, нет локальной версии)
const API_BASE_URL = process.env.NEXT_PUBLIC_MAIN_API_URL

/**
 * 📝 Типы данных
 */
export interface LoginRequest {
  login: string
  password: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface User {
  id: number
  name: string
  login: string
  role: number
  objectState?: number
  lastLogin?: string
  created?: string
  updated?: string
}

/**
 * 📝 Типы для групп пользователя
 */
export interface Group {
  id: number
  name: string
  assistantId: number | null
  imageId: number | null
  disableAi: boolean
  objectState?: number
  ragIndex?: string | null
}

export interface UserGroup {
  userId: number
  user: User
  groupId: number
  group: Group
  joinedAt: string
  role: number
  invitedByUser: number
  objectState?: number
}

/**
 * ❌ Ошибка API
 */
export class ApiError extends Error {
  public status: number
  public data: any
  
  constructor(status: number, message: string, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * 🔒 Глобальный обработчик 401 Unauthorized
 * Регистрируется один раз из UserProvider при инициализации приложения.
 * Вызывается автоматически при любом 401-ответе (кроме эндпоинта логина).
 */
type UnauthorizedHandler = () => void
let _unauthorizedHandler: UnauthorizedHandler | null = null

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null): void => {
  _unauthorizedHandler = handler
}

/**
 * 🔧 Базовый запрос к API
 */
const apiRequest = async (endpoint: string, options: RequestInit = {}, expectBlob: boolean = false): Promise<any> => {
  const url = `${API_BASE_URL}${endpoint}`
  const method = options.method || 'GET'
  
  // Формируем заголовки
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }
  
  // 🐛 ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ЗАПРОСА
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', `🚀 API REQUEST: ${method} ${endpoint}`)
    debugLog('api', '📍 URL:', url)
    debugLog('api', '🔧 Method:', method)
    debugLog('api', '📋 Headers:', headers)
    debugLog('api', '📂 Expect Blob:', expectBlob)
    
    if (options.body) {
      debugLog('api', '📦 Body:', options.body)
      try {
        const parsedBody = JSON.parse(options.body as string)
        debugLog('api', '📄 Parsed Body:', parsedBody)
      } catch {
        debugLog('api', '📄 Raw Body:', options.body)
      }
    }
    debugGroupEnd('api')
  } else {
    debugLog('api', `📡 ${method} ${url}`)
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    })
    
    // 🐛 ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ОТВЕТА
    if (DEBUG.shouldLog('api')) {
      debugGroup('api', `📨 API RESPONSE: ${response.status} ${response.statusText}`)
      debugLog('api', '📊 Status:', response.status, response.statusText)
      debugLog('api', '🔗 URL:', response.url)
      debugLog('api', '✅ OK:', response.ok)
      
      // Логируем заголовки ответа
      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })
      debugLog('api', '📋 Response Headers:', responseHeaders)
    } else {
      debugLog('api', `📥 ${response.status} ${response.statusText}`)
    }
    
    if (!response.ok) {
      // Считываем тело как текст ОДИН раз
      const errorText = await response.text()
      let parsed: any | null = null
      let messageFromServer: string = ''

      // Пытаемся распарсить JSON, если это он
      try {
        parsed = JSON.parse(errorText)
      } catch {
        parsed = null
      }

      if (parsed && typeof parsed === 'object') {
        // Берем message из JSON, или сериализуем весь объект
        messageFromServer = (parsed.message && String(parsed.message)) || JSON.stringify(parsed)
      } else {
        // Иначе используем сырой текст, либо дефолт
        messageFromServer = errorText?.trim() || `HTTP ${response.status}`
      }

      // 🐛 ЛОГИРУЕМ ОШИБКУ
      if (DEBUG.shouldLog('api')) {
        debugLog('api', '❌ Error Body (raw):', errorText)
        if (parsed !== null) debugLog('api', '❌ Error Data (parsed):', parsed)
        debugGroupEnd('api')
      }

      // 🔒 Глобальный перехват 401: вызываем обработчик (кроме эндпоинта логина)
      if (response.status === 401 && endpoint !== '/auth/login') {
        _unauthorizedHandler?.()
      }

      // Пробрасываем текст сервера в message
      throw new ApiError(response.status, messageFromServer, parsed ?? { raw: errorText })
    }
    
    // Обрабатываем ответ в зависимости от типа
    let data
    if (expectBlob) {
      data = await response.blob()
      
      // 🐛 ЛОГИРУЕМ BLOB ОТВЕТ
      if (DEBUG.shouldLog('api')) {
        debugLog('api', '✅ Response Blob:', {
          size: data.size,
          type: data.type
        })
        debugGroupEnd('api')
      } else {
        debugLog('api', `✅ Blob получен (${data.size} bytes, ${data.type})`)
      }
    } else {
      // Проверяем, есть ли контент в ответе
      const contentLength = response.headers.get('content-length')
      
      // Если content-length ЯВНО равен '0', возвращаем null (пустое тело)
      if (contentLength === '0') {
        // 🐛 ЛОГИРУЕМ ПУСТОЙ ОТВЕТ
        if (DEBUG.shouldLog('api')) {
          debugLog('api', '✅ Response: Пустое тело (успешно)')
          debugGroupEnd('api')
        } else {
          debugLog('api', `✅ Пустой ответ (успешно)`)
        }
        return null
      }
      
      // Пытаемся прочитать как JSON
      try {
        data = await response.json()
        
        // 🐛 ЛОГИРУЕМ JSON ОТВЕТ
        if (DEBUG.shouldLog('api')) {
          debugLog('api', '✅ Response Data:', data)
          debugGroupEnd('api')
        } else {
          debugLog('api', `✅ Данные получены`)
        }
      } catch (jsonError) {
        // Если не удалось распарсить как JSON, пробуем прочитать как текст
        try {
          const text = await response.text()
          
          if (!text || text.trim() === '') {
            // Пустой текст - возвращаем null
            if (DEBUG.shouldLog('api')) {
              debugLog('api', '✅ Response: Пустое тело (успешно)')
              debugGroupEnd('api')
            } else {
              debugLog('api', `✅ Пустой ответ (успешно)`)
            }
            return null
          }
          
          // Возвращаем текст как есть
          if (DEBUG.shouldLog('api')) {
            debugLog('api', '⚠️ Не JSON, возвращаем текст:', text)
            debugGroupEnd('api')
          }
          return text
        } catch (textError) {
          // Вообще не удалось прочитать ответ - возвращаем null
          if (DEBUG.shouldLog('api')) {
            debugLog('api', '✅ Response: Не удалось прочитать тело (возвращаем null)')
            debugGroupEnd('api')
          }
          return null
        }
      }
    }
    
    return data
    
  } catch (error) {
    // 🐛 ЛОГИРУЕМ СЕТЕВУЮ ОШИБКУ
    if (DEBUG.shouldLog('api') && !(error instanceof ApiError)) {
      debugGroup('api', '💥 NETWORK ERROR')
      debugError('Error object:', error)
      debugError('Error message:', error instanceof Error ? error.message : String(error))
      debugError('Error stack:', error instanceof Error ? error.stack : 'No stack')
      debugGroupEnd('api')
    }
    
    if (error instanceof ApiError) throw error
    console.error(`💥 Сетевая ошибка:`, error)
    toast.error("Сетевая ошибка", { description:"Проверьте подключение к интернету", duration: 5000 })
    throw new Error(`Не удалось подключиться к серверу: ${error}`)
  }
}

/**
 * 🔑 Авторизация
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  return apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  })
}

// ❌ ОБНОВЛЕНИЕ ТОКЕНА УДАЛЕНО
// Refresh токены небезопасны на клиенте

/**
 * 👤 Данные пользователя
 */
export const getUser = async (accessToken: string): Promise<User> => {
  return apiRequest('/user', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 👥 Группы пользователя
 */
export const getGroups = async (accessToken: string): Promise<UserGroup[]> => {
  return apiRequest('/groups', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 👥 Получить всех пользователей в группе
 */
export const getGroupUsers = async (accessToken: string, groupId: number): Promise<UserGroup[]> => {
  return apiRequest(`/groups/${groupId}/users`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 👤 Обновить данные пользователя
 */
export interface UserUpdateRequest {
  name?: string
  login?: string
  password?: string
}

export const updateUser = async (
  accessToken: string,
  userId: number,
  data: UserUpdateRequest
): Promise<any> => {
  return apiRequest(`/user/update?id=${userId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 👤 Создать нового пользователя
 */
export interface UserCreateRequest {
  name: string
  login: string
  password: string
}

export const createUser = async (
  accessToken: string,
  data: UserCreateRequest
): Promise<User> => {
  return apiRequest(`/user/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 👥 Обновить роль пользователя в группе
 */
export interface UpdateUserGroupRequest {
  role: number // 1 — User, 2 — Manager, 4 — Administrator, 8 — Owner
}

export const updateUserRoleInGroup = async (
  accessToken: string,
  groupId: number,
  userId: number,
  role: number
): Promise<UserGroup> => {
  return apiRequest(`/groups/${groupId}/user/${userId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  })
}

/**
 * 👥 Пригласить пользователя в группу
 */
export interface GroupInviteRequest {
  userId: number // ID пользователя, которого приглашаем
  role: number // 1 — User, 2 — Manager, 4 — Administrator, 8 — Owner
}

export const inviteUserToGroup = async (
  accessToken: string,
  groupId: number,
  userId: number,
  role: number = 1
): Promise<any> => {
  return apiRequest(`/groups/${groupId}/invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, role }),
  })
}

/**
 * 📝 Типы для инструкций
 */
export interface Position {
  x: number
  y: number
  z: number
}

export interface Rotation {
  x: number
  y: number
  z: number
}

export interface Note {
  id: number
  aiId: number | null
  content: string
  position: Position
  instructionId: number
  created: string
  updated: string
  createdBy: number
  objectState: number
}

export interface Attachment {
  id: number
  type: number
  contentId: number
  index: number
  metadata: any
  scenarioStepId: number
  objectState: number
  contentText: string | null
  subtitlesId: number | null
  content?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    groupId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

export interface CloudAnchorFallback {
  id: number
  imageContentId: number
  scale: number
  position: Position
  anchorPosition: Position
  anchorRotation: Rotation
}

export interface Marker {
  id: number
  type: number
  content: any
  assetId: number | null
  position: Position
  rotation: Rotation
  width: number
  objectState: number
  instructionId: number
}

export interface Instruction {
  id: number
  name: string
  description: string | null
  created: string
  updated: string
  previewImageId: number | null
  cloudAnchorId: number
  cloudAnchorFallback: CloudAnchorFallback
  groupId: number
  creatorId: number
  objectState: number
  markers: Marker[]
  previewImage?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    groupId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

export interface InstructionData {
  id: number
  notes: Note[]
  scenarios: Scenario[]
}

/**
 * 📚 Получить список инструкций группы
 */
export const getGroupInstructions = async (accessToken: string, groupId: number): Promise<Instruction[]> => {
  return apiRequest(`/groups/${groupId}/instructions`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 📚 Получить инструкцию по ID (заголовок)
 */
export const getInstruction = async (accessToken: string, instructionId: number): Promise<Instruction> => {
  return apiRequest(`/instructions/${instructionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 📚 Получить данные инструкции
 */
export const getInstructionData = async (accessToken: string, instructionId: number): Promise<InstructionData> => {
  return apiRequest(`/instructions/${instructionId}/data`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 📝 Создать инструкцию
 */
export interface InstructionCreateRequest {
  name: string
  description?: string | null
  group: number
  cloudAnchorId: number
  previewImageId?: number | null
}

export const createInstruction = async (
  accessToken: string,
  data: InstructionCreateRequest
): Promise<Instruction> => {
  return apiRequest('/instructions/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 🗑️ Удалить инструкцию
 */
export const deleteInstruction = async (
  accessToken: string,
  instructionId: number
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * ✏️ Обновить инструкцию
 */
export interface InstructionUpdateRequest {
  name?: string | null
  description?: string | null
  cloudAnchorId?: number | null
  previewImageId?: number | null
}

export const updateInstruction = async (
  accessToken: string,
  instructionId: number,
  data: InstructionUpdateRequest
): Promise<Instruction> => {
  return apiRequest(`/instructions/${instructionId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 📊 Типы для аналитики
 */
export interface AnalyticsParams {
  group?: number
  begin?: number
  end?: number
  today?: number
  year?: number
  month?: number
  day?: number
  user?: number | number[]  // Может быть числом или массивом чисел
  from?: number
  to?: number
  scenario?: number
  count?: number
  event?: string | string[]  // Может быть строкой или массивом строк
  type?: number
  skip?: number
  take?: number
  sort?: number
  query?: string | string[]  // Может быть строкой или массивом строк
  ignoreAdmins?: boolean
  ignoreDismissed?: boolean
  session?: number
}

export interface ScenarioRankItem {
  idStr: string | null
  count: number
}

export interface AnalyticsEvent {
  id: number
  userId: number
  userLogin?: string  // Логин пользователя (опционально, может отсутствовать)
  name: string
  time: number
  timeZone: number
  type: number
  value: any
  groupId: number | null
  deviceId: string | null
  platform: string | null
  userAgent: string | null
  sessionId: number | null
}

export interface AnalyticsSession {
  id: number
  userId: number
  userLogin: string | null
  groupId: number | null
  deviceId: string | null
  platform: number | null
  userAgent: string | null
  timeZone: number
  beginTime: number
  endTime: number
  isClosed: boolean
  isPaused: boolean
}

// Интерфейсы для ответов на вопросы в формах (из dataJson репортов)
export interface FormReportQuestion {
  questionId: number
  questionType: 1 | 2 | 3 | 4 | 5 | 6  // 1=текст, 2=число, 3=выбор, 4=фото, 5=несколько из списка, 6=подпись
  isSuccess?: boolean
  completedAt: number
  
  // Для type: 1 (текст) и type: 2 (число)
  answer?: string
  rightAnswer?: string
  
  // Для type: 3 (выбор одного)
  rightAnswers?: string[]
  answerId?: number
  answerContent?: string
  
  // Для type: 5 (несколько из списка)
  answerIds?: number[]
  answerContents?: string[]
  
  // Для type: 4 (фото с AI)
  aiFeedback?: string
  aiResult?: boolean
  aiConfidence?: number
  photoId?: number

  // Для type: 6 (подпись): ID ассета с PNG подписи и момент нажатия «Отправить»
  signatureId?: number
  signedAt?: number
}

export interface FormReport {
  formId: number
  questions: FormReportQuestion[]
}

export interface ScenarioReport {
  id: number
  instructionId: number
  scenarioId: number
  scenarioName?: string
  instructionName?: string
  userId: number
  userLogin?: string
  userName?: string
  dataId: number
  startTime: number
  endTime: number
  totalTime: number
  dataJson?: string
  completedSteps?: number | null
  totalSteps?: number | null
  isCompleted?: boolean | null
}

export interface ScenarioReportsResponse {
  totalCount: number
  result: ScenarioReport[]
}

// 📦 Файловые ассеты
export interface Asset {
  id: number
  name: string
  type: string
  extension: string
  metadata: string | null
  shared: boolean
  createdAt: string
  updatedAt: string
  s3Id: string
  uploadedBy: number
  groupId: number | null
  url: string
}

// 📘 Процедура и шаги процедуры

// Интерфейс для образцов изображений в вопросах
export interface FormQuestionImageSample {
  id: number
  index: number
  imageId: number
  isRight: boolean
  objectState: number
  formQuestionId: number
  image?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    groupId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

// Интерфейс для вариантов ответа (для type: 3 - выбор)
export interface FormQuestionAnswer {
  id: number
  formQuestionId: number
  index: number
  text: string
  imageId: number
  isRight: boolean
  objectState: number
}

// Интерфейс для вопроса в форме
export interface FormQuestion {
  id: number
  formId: number
  index: number
  type: 1 | 2 | 3 | 4 | 5 | 6  // 1=текст, 2=число, 3=выбор, 4=фото, 5=несколько из списка, 6=подпись
  data: string  // Текст вопроса
  required: boolean
  hasRightAnswer: boolean
  rightAnswer: string | null
  showRightAnswer: boolean
  caseSensitive: boolean
  aiValidation: boolean
  aiPrompt: string | null
  answers: FormQuestionAnswer[]  // Варианты ответа для type: 3
  rightImageSamples: FormQuestionImageSample[]  // Правильные образцы для type: 4
  wrongImageSamples: FormQuestionImageSample[]  // Неправильные образцы для type: 4
  objectState: number
}

// Интерфейс для формы
export interface ScenarioForm {
  id: number
  scenarioStepId: number
  questions: FormQuestion[]
  objectState: number
}

export interface ScenarioStep {
  id: number
  description?: string | null
  detailedDescription?: string | null
  index?: number
  forms?: ScenarioForm[]  // Формы с вопросами
  attachments?: any[]
  requireAnswer?: boolean
  requirePhotoReport?: boolean
  position?: {
    x: number
    y: number
    z: number
  }
}

export interface Scenario {
  id: number
  name: string
  description?: string | null
  created: string
  updated: string
  createdBy: number
  instructionId: number
  aiFileId?: number
  steps?: ScenarioStep[]
}

/**
 * 📝 Типы для управления процедурами
 */

// Создание процедуры
export interface ScenarioCreateRequest {
  name: string
  description?: string | null
}

// Обновление процедуры
export interface ScenarioUpdateRequest {
  name?: string | null
  description?: string | null
}

// Создание шага
export interface ScenarioStepCreateRequest {
  description?: string | null
  position?: { x: number, y: number, z: number }
  index?: number
  requireAnswer?: boolean
  requirePhotoReport?: boolean
}

// Обновление шага
export interface ScenarioStepUpdateRequest {
  description?: string | null
  detailedDescription?: string | null
  position?: { x: number, y: number, z: number } | null
  index?: number | null
  requireAnswer?: boolean | null
  requirePhotoReport?: boolean | null
}

// Создание вопроса
export interface FormQuestionCreateRequest {
  index?: number
  type: 1 | 2 | 3 | 4 | 5 | 6  // 0 None; 1 Text; 2 Number; 3 Variants; 4 Photo; 5 MultipleVariants; 6 Signature (only data + required apply)
  data?: string | null
  aiValidation?: boolean | null
  aiPrompt?: string | null
  caseSensitive?: boolean | null
  required?: boolean | null
  hasRightAnswer?: boolean | null
  rightAnswer?: string | null
  showRightAnswer?: boolean | null
}

// Обновление вопроса
export interface FormQuestionUpdateRequest {
  index?: number | null
  type?: 1 | 2 | 3 | 4 | 5 | 6 | null
  data?: string | null
  aiValidation?: boolean | null
  aiPrompt?: string | null
  caseSensitive?: boolean | null
  required?: boolean | null
  hasRightAnswer?: boolean | null
  rightAnswer?: string | null
  showRightAnswer?: boolean | null
  isNull?: boolean
}

// Создание вложения
export interface AttachmentCreateRequest {
  type: 0 | 1 | 2 | 3  // 0 None; 1 Photo; 2 Video; 3 File
  contentId: number
  index?: number
  metadata?: string | null
  contentText?: string | null
  subtitlesId?: number | null
}

// Обновление вложения
export interface AttachmentUpdateRequest {
  type?: 0 | 1 | 2 | 3 | null
  contentId?: number | null
  index?: number | null
  metadata?: string | null
  contentText?: string | null
  subtitlesId?: number | null
}

// Создание варианта ответа
export interface FormQuestionAnswerCreateRequest {
  index: number
  text?: string | null
  imageId?: number | null
  isRight?: boolean | null
  isValid?: boolean
}

// Обновление варианта ответа
export interface FormQuestionAnswerUpdateRequest {
  index?: number | null
  text?: string | null
  imageId?: number | null
  isRight?: boolean | null
  isNull?: boolean
}

/**
 * 📊 АНАЛИТИКА
 */

// Скачать файл метрик Excel
export const downloadMetrics = async (accessToken: string, params: AnalyticsParams = {}): Promise<Blob> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.today !== undefined) queryParams.append('today', params.today.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/download${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  }, true) // expectBlob = true
}

/**
 * 📁 Утилита для скачивания файла в браузере
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  // Создаем URL для blob
  const url = window.URL.createObjectURL(blob)
  
  // Создаем временную ссылку для скачивания
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  
  // Добавляем в DOM, кликаем и удаляем
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  // Освобождаем URL
  window.URL.revokeObjectURL(url)
  
  debugLog('api', `📁 Файл скачан: ${filename} (${blob.size} bytes)`)
}

// Дневные активные пользователи
export const getDailyActiveUsers = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.year !== undefined) queryParams.append('year', params.year.toString())
  if (params.month !== undefined) queryParams.append('month', params.month.toString())
  if (params.day !== undefined) queryParams.append('day', params.day.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/dau${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Месячные активные пользователи
export const getMonthlyActiveUsers = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.year !== undefined) queryParams.append('year', params.year.toString())
  if (params.month !== undefined) queryParams.append('month', params.month.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/mau${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Запуски приложения пользователем
export const getUserLaunches = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/user/launches${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Среднее время активных пользователей (в миллисекундах)
export const getAverageTime = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/time/average/users${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Активные пользователи в периоде времени
export const getActiveUsers = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/au${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество открытых процедур
export const getScenariosCount = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.scenario !== undefined) queryParams.append('scenario', params.scenario.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Процедуры пользователя
export const getUserScenarios = async (accessToken: string, params: AnalyticsParams = {}): Promise<number> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/user/scenarios${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Рейтинг процедур
export const getScenariosRank = async (accessToken: string, params: AnalyticsParams = {}): Promise<ScenarioRankItem[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())
  if (params.count !== undefined) queryParams.append('count', params.count.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/rank${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество открытий процедур по дням за период
export const getScenariosRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/range${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество запусков приложения по дням за период
export const getApplicationLaunchesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/launches/range${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество активных инструкций по дням за период
export const getActiveInstructionsRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/instructions${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество активных процедур по дням за период
export const getActiveScenariosRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/scenarios${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество изменений процедур по дням за период
export const getScenariosChangesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/changes${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество запросов к Copilot по дням за период
export const getCopilotRequestsRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/copilot/requests${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// Количество активных AI-файлов по дням за период
export const getActiveAiFilesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/ai/files${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// События аналитики
export const getEvents = async (accessToken: string, params: AnalyticsParams = {}, controller?: AbortController): Promise<AnalyticsEvent[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  
  // Поддержка массива event (event=value1&event=value2)
  if (params.event !== undefined) {
    if (Array.isArray(params.event)) {
      params.event.forEach(e => {
        if (e && e.trim() !== '') queryParams.append('event', e)
      })
    } else if (params.event.trim() !== '') {
      queryParams.append('event', params.event)
    }
  }
  
  if (params.type !== undefined) queryParams.append('type', params.type.toString())
  
  // Поддержка массива user (user=id1&user=id2)
  if (params.user !== undefined) {
    if (Array.isArray(params.user)) {
      params.user.forEach(u => queryParams.append('user', u.toString()))
    } else {
      queryParams.append('user', params.user.toString())
    }
  }
  
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  
  // Поддержка массива query (query=search1&query=search2)
  if (params.query !== undefined) {
    if (Array.isArray(params.query)) {
      params.query.forEach(q => {
        if (q && q.trim() !== '') queryParams.append('query', q)
      })
    } else if (params.query.trim() !== '') {
      queryParams.append('query', params.query)
    }
  }
  
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  if (params.session !== undefined) queryParams.append('session', params.session.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
    signal: controller?.signal,
  })
}

export const getSessions = async (accessToken: string, params: AnalyticsParams = {}, controller?: AbortController): Promise<AnalyticsSession[]> => {
  const queryParams = new URLSearchParams()
  if (params.group !== undefined) queryParams.append('group', params.group.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/sessions${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
    signal: controller?.signal,
  })
}

// Получить все отчёты по запускам процедур в группе
export const getScenarioReports = async (
  accessToken: string, 
  groupId: number, 
  params: { 
    sort?: number; 
    skip?: number; 
    take?: number; 
    begin?: number; 
    end?: number;
    scenario?: number;
    user?: number;
  } = {}
): Promise<ScenarioReportsResponse> => {
  const queryParams = new URLSearchParams()
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.scenario !== undefined) queryParams.append('scenario', params.scenario.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/groups/${groupId}/reports${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 📘 Получить процедуру (с шагами) по instructionId и scenarioId
 * GET /instructions/{instructionId}/scenarios/{id}
 */
export const getScenario = async (
  accessToken: string | null | undefined,
  instructionId: number,
  scenarioId: number
): Promise<Scenario> => {
  const headers: Record<string, string> = {}
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}`, {
    method: 'GET',
    headers,
  })
}

/**
 * 📤 Загрузить ассет (файл)
 * POST /assets/upload
 */
export interface UploadAssetParams {
  name: string
  type: string
  groupId: number
  file: File
  metadata?: any
  shared?: boolean
  onProgress?: (progress: number) => void
  signal?: AbortSignal
}

export const uploadAsset = async (
  accessToken: string,
  params: UploadAssetParams
): Promise<Asset> => {
  const url = `${API_BASE_URL}/assets/upload`
  
  // 🐛 DEBUG LOGGING
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', '📤 UPLOAD ASSET REQUEST')
    debugLog('api', '📍 URL:', url)
    debugLog('api', '📁 File:', params.file.name, `(${Math.round(params.file.size / 1024)} KB)`)
    debugLog('api', '🏷️ Name:', params.name)
    debugLog('api', '📦 Type:', params.type)
    debugLog('api', '🏢 Group:', params.groupId)
    debugGroupEnd('api')
  } else {
    debugLog('api', `📤 Uploading asset: ${params.file.name}`)
  }
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let lastLoggedPercent = 0
    
    // Отслеживание прогресса загрузки
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        
        // Логируем каждые 10%
        if (DEBUG.shouldLog('api') && percentComplete >= lastLoggedPercent + 10) {
          debugLog('api', `📊 Upload Progress: ${percentComplete}% (${Math.round(event.loaded / 1024)} KB / ${Math.round(event.total / 1024)} KB)`)
          lastLoggedPercent = percentComplete
        }
        
        if (params.onProgress) {
          params.onProgress(percentComplete)
        }
      }
    })
    
    // Обработка успешного завершения
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          
          // 🐛 DEBUG LOGGING
          if (DEBUG.shouldLog('api')) {
            debugGroup('api', '✅ UPLOAD ASSET SUCCESS')
            debugLog('api', '🆔 Asset ID:', result.id)
            debugLog('api', '📦 S3 ID:', result.s3Id)
            debugLog('api', '🔗 URL:', result.url)
            debugGroupEnd('api')
          } else {
            debugLog('api', `✅ Asset uploaded: ID ${result.id}`)
          }
          
          resolve(result)
        } catch (error) {
          debugError('api', '❌ Parse error:', error)
          reject(new Error('Ошибка парсинга ответа сервера'))
        }
      } else {
        debugError('api', `❌ HTTP ${xhr.status}:`, xhr.statusText)
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })
    
    // Обработка ошибок
    xhr.addEventListener('error', () => {
      debugError('api', '💥 Network error during upload')
      reject(new Error('Ошибка сетевого соединения'))
    })
    
    // Обработка отмены
    xhr.addEventListener('abort', () => {
      debugLog('api', '⚠️ Upload aborted')
      reject(new Error('Загрузка отменена'))
    })
    
    // Поддержка AbortSignal
    if (params.signal) {
      params.signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }
    
    // Формируем данные
    const formData = new FormData()
    formData.append('Name', params.name)
    formData.append('Type', params.type)
    formData.append('GroupId', params.groupId.toString())
    formData.append('File', params.file)
    if (params.metadata) formData.append('Metadata', JSON.stringify(params.metadata))
    if (params.shared !== undefined) formData.append('Shared', String(params.shared))
    
    // Отправляем запрос
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.send(formData)
  })
}

/**
 * 🤖 AI-валидация фотографии
 * POST /ai/photo/form/question
 */
export interface PhotoValidationRequest {
  questionId: number
  photoAssetId: number
}

export interface PhotoValidationResult {
  result: boolean
  confidence: number
  feedback: string
}

export const validatePhoto = async (
  accessToken: string,
  request: PhotoValidationRequest
): Promise<PhotoValidationResult> => {
  // 🐛 DEBUG LOGGING
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', '🤖 AI PHOTO VALIDATION REQUEST')
    debugLog('api', '📍 Endpoint:', '/ai/photo/form/question')
    debugLog('api', '❓ Question ID:', request.questionId)
    debugLog('api', '🖼️ Asset ID:', request.photoAssetId)
    debugGroupEnd('api')
  } else {
    debugLog('api', `🤖 Validating photo: Q${request.questionId}, Asset${request.photoAssetId}`)
  }
  
  const result = await apiRequest('/ai/photo/form/question', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
  
  // 🐛 DEBUG LOGGING RESULT
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', '✅ AI VALIDATION RESULT')
    debugLog('api', '🎯 Result:', result.result ? 'PASS ✅' : 'FAIL ❌')
    debugLog('api', '📊 Confidence:', `${Math.round(result.confidence * 100)}%`)
    debugLog('api', '💬 Feedback:', result.feedback)
    debugGroupEnd('api')
  } else {
    debugLog('api', `✅ AI result: ${result.result ? 'PASS' : 'FAIL'} (${Math.round(result.confidence * 100)}%)`)
  }
  
  return result
}

/**
 * 📊 Отправить отчет по процедуре
 * POST /instructions/{instructionId}/scenarios/{scenarioId}/reports/create
 */

// Тип для элемента timeline (новая архитектура хронологии)
export interface TimelineSession {
  stepId: number
  startTime: number   // Время начала сессии в миллисекундах
  endTime: number     // Время окончания сессии в миллисекундах
  marked?: boolean    // Флаг завершения шага в этой сессии
  photoId?: number
  answer?: string
  formReport?: {
    formId: number
    questions: Array<{
      questionId: number
      questionType: 1 | 2 | 3 | 4 | 5 | 6
      isSuccess?: boolean
      completedAt: number
      answer?: string
      rightAnswer?: string
      rightAnswers?: string[]
      answerId?: number
      answerContent?: string
      answerIds?: number[]
      answerContents?: string[]
      aiFeedback?: string
      aiResult?: boolean
      aiConfidence?: number
      photoId?: number
    }>
  }
}

export interface ScenarioReportData {
  newFormat?: boolean  // Флаг новой версии архитектуры
  steps: Array<{       // Старый формат для обратной совместимости
    stepId: number
    endTime: number
    photoId?: number
    answer?: string
    formReport?: {
      formId: number
      questions: Array<{
        questionId: number
        questionType: 1 | 2 | 3 | 4 | 5 | 6
        isSuccess?: boolean
        completedAt: number
        answer?: string
        rightAnswer?: string
        rightAnswers?: string[]
        answerId?: number
        answerContent?: string
        answerIds?: number[]
        answerContents?: string[]
        aiFeedback?: string
        aiResult?: boolean
        aiConfidence?: number
        photoId?: number
      }>
    }
  }>
  timeline?: TimelineSession[]  // Новое поле с полной хронологией сессий
}

export interface SubmitScenarioReportParams {
  instructionId: number
  scenarioId: number
  start: number  // Unix UTC миллисекунды
  end: number    // Unix UTC миллисекунды
  timezone: number  // Часовой пояс (например 3 для UTC+3)
  steps: number  // Выполнено шагов
  total: number  // Всего шагов
  completed: boolean
  reportData: ScenarioReportData
}

export const submitScenarioReport = async (
  accessToken: string,
  params: SubmitScenarioReportParams
): Promise<any> => {
  const queryParams = new URLSearchParams()
  queryParams.append('start', params.start.toString())
  queryParams.append('end', params.end.toString())
  queryParams.append('timezone', params.timezone.toString())
  queryParams.append('steps', params.steps.toString())
  queryParams.append('total', params.total.toString())
  queryParams.append('completed', String(params.completed))
  
  const url = `/instructions/${params.instructionId}/scenarios/${params.scenarioId}/reports/create?${queryParams.toString()}`
  
  // 🐛 DEBUG LOGGING
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', '📊 SUBMIT SCENARIO REPORT')
    debugLog('api', '📍 URL:', url)
    debugLog('api', '📋 Instruction:', params.instructionId)
    debugLog('api', '🎬 Scenario:', params.scenarioId)
    debugLog('api', '📊 Steps:', `${params.steps}/${params.total}`)
    debugLog('api', '✅ Completed:', params.completed)
    debugLog('api', '📦 Report:', params.reportData)
    debugGroupEnd('api')
  } else {
    debugLog('api', `📊 Submitting report: Scenario ${params.scenarioId}, ${params.steps}/${params.total} steps`)
  }
  
  return apiRequest(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params.reportData),
  })
}

/**
 * 🎬 УПРАВЛЕНИЕ ПРОЦЕДУРАМИ
 */

// 1) Создать процедуру
export const createScenario = async (
  accessToken: string,
  instructionId: number,
  data: ScenarioCreateRequest
): Promise<Scenario> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 2) Обновить шапку процедуры
export const updateScenario = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  data: ScenarioUpdateRequest
): Promise<Scenario> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 2.1) Удалить процедуру
export const deleteScenario = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 3) Создать шаг
export const createScenarioStep = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  data: ScenarioStepCreateRequest
): Promise<ScenarioStep> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 4) Обновить конкретный шаг
export const updateScenarioStep = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  stepId: number,
  data: ScenarioStepUpdateRequest
): Promise<ScenarioStep> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${stepId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 5) Удалить шаг
export const deleteScenarioStep = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  stepId: number
): Promise<ScenarioStep> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${stepId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 6) Изменить порядок шагов
export const updateStepsIndexes = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  stepIds: number[]
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(stepIds),
  })
}

// 6) Обновить вложение
export const updateAttachment = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  stepId: number,
  attachmentId: number,
  data: AttachmentUpdateRequest
): Promise<Attachment> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${stepId}/attachments/${attachmentId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 7) Изменить порядок вложений
export const updateAttachmentsIndexes = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  stepId: number,
  attachmentIds: number[]
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${stepId}/attachments/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(attachmentIds),
  })
}

// 6) Добавить форму на шаг
export const createStepForm = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number
): Promise<ScenarioForm> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 7) Удалить форму на шаге
export const deleteStepForm = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number
): Promise<ScenarioForm> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 8) Добавить вопрос в форму
export const createFormQuestion = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  data: FormQuestionCreateRequest
): Promise<FormQuestion> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 9) Обновить вопрос в форме
export const updateFormQuestion = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  data: FormQuestionUpdateRequest
): Promise<FormQuestion> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 10) Удалить вопрос из формы
export const deleteFormQuestion = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number
): Promise<FormQuestion> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 11) Добавить вложение в шаг
export const createStepAttachment = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  data: AttachmentCreateRequest
): Promise<Attachment> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/attachments/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 12) Удалить вложение из шага
export const deleteStepAttachment = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  attachmentId: number
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 13) Обновить вложение
export const updateStepAttachment = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  attachmentId: number,
  data: AttachmentUpdateRequest
): Promise<Attachment> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/attachments/${attachmentId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 14) Добавить вариант ответа
export const createQuestionAnswer = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  data: FormQuestionAnswerCreateRequest
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/answers/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 15) Обновить вариант ответа
export const updateQuestionAnswer = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  answerId: number,
  data: FormQuestionAnswerUpdateRequest
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/answers/${answerId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 16) Удалить вариант ответа
export const deleteQuestionAnswer = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  answerId: number
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/answers/${answerId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 17) Добавить образец изображения к вопросу
export const createQuestionImageSample = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  data: {
    index: number
    imageId: number
    isRight: boolean
  }
): Promise<FormQuestionImageSample> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/samples/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// 18) Удалить образец изображения
export const deleteQuestionImageSample = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  sampleId: number
): Promise<FormQuestionImageSample> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/samples/${sampleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// 8) Изменить порядок образцов изображений
export const updateQuestionImageSamplesIndexes = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  sampleIds: number[],
  isRight: boolean
): Promise<void> => {
  const sampleType = isRight ? 'right' : 'wrong'
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/samples/indexes/${sampleType}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sampleIds),
  })
}

// 19) Изменить порядок вопросов в форме (массовое обновление индексов)
export const updateFormQuestionsIndexes = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionIds: number[]
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(questionIds),
  })
}

// 20) Изменить порядок вариантов ответа (массовое обновление индексов)
export const updateQuestionAnswersIndexes = async (
  accessToken: string,
  instructionId: number,
  scenarioId: number,
  scenarioStepId: number,
  formId: number,
  questionId: number,
  answerIds: number[]
): Promise<void> => {
  return apiRequest(`/instructions/${instructionId}/scenarios/${scenarioId}/steps/${scenarioStepId}/forms/${formId}/questions/${questionId}/answers/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(answerIds),
  })
}

/**
 * 📦 Получить информацию об ассете по ID
 * GET /assets/{id}
 */
export const getAsset = async (
  accessToken: string | null | undefined,
  id: number
): Promise<Asset> => {
  const headers: Record<string, string> = {}
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return apiRequest(`/assets/${id}`, {
    method: 'GET',
    headers,
  })
}

/**
 * 📅 ВСТРЕЧИ - СЕРВЕР ТРАНСКРИБАЦИИ
 */

// Определение URL сервера встреч
const getMeetingsServerUrl = (): string | undefined => {
  const useLocal = process.env.NEXT_PUBLIC_USE_LOCAL_MEETINGS === 'true' || process.env.USE_LOCAL_MEETINGS === 'true'
  
  // В production всегда используем продакшн сервер
  if (process.env.NODE_ENV === 'production') {
    return process.env.NEXT_PUBLIC_MEETINGS_SERVER_URL
  }
  
  // В development проверяем флаг USE_LOCAL_MEETINGS
  if (useLocal) {
    return process.env.NEXT_PUBLIC_LOCAL_MEETINGS_SERVER_URL
  }
  
  return process.env.NEXT_PUBLIC_MEETINGS_SERVER_URL
}

const LOCAL_SERVER_URL = getMeetingsServerUrl()

// Локальный API запрос (без авторизации)
const localApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const url = `${LOCAL_SERVER_URL}${endpoint}`
  const method = options.method || 'GET'
  
  // Используем систему логирования debug.ts
  if (DEBUG.shouldLog('api')) {
    debugGroup('api', `🏠 LOCAL API REQUEST: ${method} ${endpoint}`)
    debugLog('api', '📍 URL:', url)
    debugLog('api', '🔧 Method:', method)
    debugLog('api', '📋 Headers:', options.headers)
    
    if (options.body) {
      debugLog('api', '📦 Body:', options.body)
    }
  } else {
    debugLog('api', `🏠 ${method} ${url}`)
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })
    
    // Логирование ответа
    if (DEBUG.shouldLog('api')) {
      debugLog('api', '📊 Status:', response.status, response.statusText)
      debugLog('api', '✅ OK:', response.ok)
    }
    
    if (!response.ok) {
      const errorText = await response.text()
      let parsed: any | null = null
      
      try {
        parsed = JSON.parse(errorText)
      } catch {
        parsed = null
      }
      
      const messageFromServer = parsed?.error || errorText || `HTTP ${response.status}`
      
      if (DEBUG.shouldLog('api')) {
        debugLog('api', '❌ Error Body:', errorText)
        if (parsed) debugLog('api', '❌ Error Data:', parsed)
        debugGroupEnd('api')
      }
      
      throw new ApiError(response.status, messageFromServer, parsed)
    }
    
    const data = await response.json()
    
    if (DEBUG.shouldLog('api')) {
      debugLog('api', '✅ Response Data:', data)
      debugGroupEnd('api')
    } else {
      debugLog('api', `📥 Response:`, response.status, response.statusText)
    }
    
    return data
    
  } catch (error) {
    if (DEBUG.shouldLog('api') && !(error instanceof ApiError)) {
      debugGroup('api', '💥 LOCAL SERVER ERROR')
      debugError('Error object:', error)
      debugError('Error message:', error instanceof Error ? error.message : String(error))
      debugGroupEnd('api')
    }
    
    if (error instanceof ApiError) throw error
    console.error(`💥 Ошибка локального сервера:`, error)
    throw new Error(`Не удалось подключиться к серверу встреч: ${error}`)
  }
}

/**
 * 📝 Типы для встреч
 */
export interface ServerMeeting {
  id: string
  title: string
  originalFileName: string
  filePath: string
  audioPath?: string
  fileSize: number
  duration?: number
  status: 'uploading' | 'processing' | 'completed' | 'error'
  progress: number
  source: 'upload'
  createdAt: string
  updatedAt: string
  transcriptionId?: string
  error?: string
}

export interface MeetingStatus {
  success: boolean
  status: string
  progress: number
  activeProcess?: {
    stage: string
    percent: number
    message: string
  }
  error?: string
}

// Получить все встречи
export const getMeetings = async (): Promise<{ success: boolean; meetings: ServerMeeting[] }> => {
  return localApiRequest('/api/meetings')
}

// Получить статус встречи
export const getMeetingStatus = async (meetingId: string): Promise<MeetingStatus> => {
  return localApiRequest(`/api/meetings/${meetingId}/status`)
}

// Получить конкретную встречу
export const getMeeting = async (meetingId: string): Promise<{ success: boolean; meeting: ServerMeeting; transcription?: any }> => {
  return localApiRequest(`/api/meetings/${meetingId}`)
}

// Обновить название встречи
export const updateMeetingTitle = async (meetingId: string, title: string): Promise<{ success: boolean; meeting: ServerMeeting }> => {
  return localApiRequest(`/api/meetings/${meetingId}/title`, {
    method: 'PUT',
    body: JSON.stringify({ title }),
  })
}

// Обновить транскрипцию встречи
export const updateMeetingTranscription = async (
  meetingId: string, 
  speakers: string[], 
  segments: any[]
): Promise<{ success: boolean; transcription: any; message?: string }> => {
  return localApiRequest(`/api/meetings/${meetingId}/transcription`, {
    method: 'PUT',
    body: JSON.stringify({ speakers, segments }),
  })
}

// Пометить встречу как ошибочную (для зависших встреч)
export const markMeetingAsError = async (meetingId: string, reason: string): Promise<{ success: boolean; meeting: ServerMeeting }> => {
  return localApiRequest(`/api/meetings/${meetingId}/error`, {
    method: 'PUT',
    body: JSON.stringify({ error: reason }),
  })
}

// Переобработать встречу заново
export const reprocessMeeting = async (meetingId: string): Promise<{ success: boolean; message: string; meetingId: string }> => {
  return localApiRequest(`/api/meetings/${meetingId}/reprocess`, {
    method: 'POST',
  })
}

// Скачать файл встречи
export const downloadMeetingFile = async (meetingId: string): Promise<void> => {
  const url = `${LOCAL_SERVER_URL}/api/meetings/${meetingId}/download`
  
  debugLog('api', `⬇️ Скачивание файла встречи: ${meetingId}`)
  
  // Используем прямую ссылку для скачивания
  const link = document.createElement('a')
  link.href = url
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 📝 САММАРИ ВСТРЕЧ - ТИПЫ И API
 */

// Типы саммари
export interface SummaryType {
  id: string
  title: string
  description: string
  emoji: string
  isDefault: boolean
}

export interface SummaryTypesResponse {
  success: boolean
  types: SummaryType[]
}

export interface MeetingSummary {
  id: string
  meetingId: string
  type: 'meeting-summary' | 'hr-interview' | 'team-sync' | 'one-on-one' | 'client-meeting' | 'custom-report'
  title: string
  content: string
  status: 'generating' | 'completed' | 'error'
  generationProgress: number
  metadata: {
    wordCount: number
    characterCount?: number
    generatedAt?: string
  }
  error: string | null
  createdAt: string
  updatedAt: string
}

export interface SummaryGenerationResponse {
  success: boolean
  message: string
  meetingId: string
  type: string
  summaryId?: string
  status?: string
}

export interface SummaryListResponse {
  success: boolean
  summaries: MeetingSummary[]
  meeting: {
    id: string
    title: string
  }
}

export interface SummaryStatusResponse {
  success: boolean
  status: string
  progress: number
  type: string
  error: string | null
}

// Получить все саммари для встречи
// Получить доступные типы summaries
export const getSummaryTypes = async (): Promise<SummaryTypesResponse> => {
  return localApiRequest('/api/summaries/types')
}

export const getMeetingSummaries = async (meetingId: string): Promise<SummaryListResponse> => {
  return localApiRequest(`/api/summaries/meeting/${meetingId}`)
}

// Получить конкретное саммари по ID
export const getSummary = async (summaryId: string): Promise<{ success: boolean; summary: MeetingSummary }> => {
  return localApiRequest(`/api/summaries/${summaryId}`)
}

// Получить статус генерации саммари
export const getSummaryStatus = async (summaryId: string): Promise<SummaryStatusResponse> => {
  return localApiRequest(`/api/summaries/${summaryId}/status`)
}

// Получить предложенные вопросы для AI-чата встречи
export const getMeetingSuggestions = async (meetingId: string): Promise<{ success: boolean; suggestions: string[] }> => {
  try {
    const result = await localApiRequest(`/api/suggestions/${meetingId}`)
    return result
  } catch (error) {
    console.error('Ошибка получения suggestions:', error)
    // В случае ошибки возвращаем дефолтные suggestions (ровно 3)
    return {
      success: true,
      suggestions: [
        'Основные решения, принятые на встрече',
        'Какие задачи и дедлайны были назначены?',
        'Ключевые проблемы и их решения'
      ]
    }
  }
}

// Запустить генерацию саммари
export const generateSummary = async (
  meetingId: string, 
  type: MeetingSummary['type'] = 'meeting-summary'
): Promise<SummaryGenerationResponse> => {
  return localApiRequest(`/api/summaries/generate/${meetingId}`, {
    method: 'POST',
    body: JSON.stringify({ type })
  })
}

// Перегенерировать существующий саммари
export const regenerateSummary = async (summaryId: string): Promise<{ success: boolean; message: string }> => {
  return localApiRequest(`/api/summaries/regenerate/${summaryId}`, {
    method: 'PUT'
  })
}

// Удалить саммари
export const deleteSummary = async (summaryId: string): Promise<{ success: boolean; message: string }> => {
  return localApiRequest(`/api/summaries/${summaryId}`, {
    method: 'DELETE'
  })
}

// Запустить генерацию кастомного саммари с произвольным системным промптом
export const generateCustomSummary = async (
  meetingId: string,
  systemPrompt: string,
  title?: string
): Promise<SummaryGenerationResponse> => {
  return localApiRequest(`/api/summaries/generate-custom/${meetingId}`, {
    method: 'POST',
    body: JSON.stringify({ systemPrompt, title })
  })
}

/**
 * 📝 Типы для AI файлов
 */
export interface AiFileAsset {
  id: number
  name: string
  type: string
  extension: string
  metadata: any
  shared: boolean
  createdAt: string
  updatedAt: string
  s3Id: string
  uploadedBy: number
  uploadedByUser?: {
    id: number
    name: string
    login: string
    role: number
    objectState: number
    lastLogin: string
    created: string
    updated: string
  }
  groupId: number | null
  objectState: number
  url: string
  fileSize: number
}

export interface AiFile {
  id: number
  fileId: string
  name: string
  extension: string
  groupId: number
  assetId: number
  asset?: AiFileAsset
  objectState: number
  isLocal: boolean
  isHidden: boolean
}

export interface GetAiFilesParams {
  group: number
  sort?: number  // >= 0 возрастание, < 0 убывание
  skip?: number
  take?: number
  hidden?: boolean
}

/**
 * 📁 Получить список AI файлов
 */
export async function getAiFiles(
  accessToken: string,
  params: GetAiFilesParams
): Promise<AiFile[]> {
  const queryParams = new URLSearchParams()
  queryParams.append('group', params.group.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.hidden !== undefined) queryParams.append('hidden', params.hidden.toString())

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/ai/files${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/**
 * 🗑️ Удалить AI файл
 * @param accessToken - токен доступа
 * @param fileId - OpenAI file ID (строка, НЕ числовой id!)
 */
export async function deleteAiFile(
  accessToken: string,
  fileId: string
): Promise<{ success: boolean; error?: string }> {
  debugGroup('api', `🗑️ FILE DELETE START: ${fileId}`)
  debugLog('api', '🆔 File ID:', fileId)
  
  try {
    const response = await apiRequest(`/ai/file/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    debugLog('api', '✅ Delete SUCCESS!')
    debugLog('api', '📦 Response:', response)
    debugGroupEnd('api')
    
    return { success: true }
  } catch (error) {
    debugError('api', '❌ Delete FAILED:', error)
    debugGroupEnd('api')
    
    const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка при удалении файла'
    return { success: false, error: errorMessage }
  }
}

/**
 * 📤 Загрузить AI файл
 * @param accessToken - токен доступа
 * @param file - файл для загрузки
 * @param groupId - ID группы
 * @param name - имя файла (без расширения)
 * @param extension - расширение файла (без точки)
 * @param onProgress - колбэк для отслеживания прогресса (0-100)
 */
export async function uploadAiFile(
  accessToken: string,
  file: File,
  groupId: number,
  name: string,
  extension: string,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; fileId?: string; error?: string }> {
  // 🐛 DEBUG LOGGING
  const fileSize = (file.size / 1024 / 1024).toFixed(2)
  debugGroup('api', `📤 FILE UPLOAD START: ${name}.${extension}`)
  debugLog('api', '📁 File:', file.name)
  debugLog('api', '📏 Size:', `${fileSize} MB`)
  debugLog('api', '🏢 Group ID:', groupId)
  debugLog('api', '🎯 Extension:', extension)
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let lastLoggedPercent = 0
    
    // Отслеживание прогресса загрузки
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        const loadedMB = (event.loaded / 1024 / 1024).toFixed(2)
        const totalMB = (event.total / 1024 / 1024).toFixed(2)
        
        // Логируем каждые 10% для уменьшения спама в консоли
        if (percentComplete >= lastLoggedPercent + 10 || percentComplete === 100) {
          debugLog('api', `📊 Upload Progress: ${percentComplete}% (${loadedMB} MB / ${totalMB} MB)`)
          lastLoggedPercent = percentComplete
        }
        
        // Вызываем колбэк прогресса (он обновляет UI)
        if (onProgress) {
          onProgress(percentComplete)
        }
      }
    })
    
    // Обработка успешного завершения
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          debugLog('api', '✅ Upload SUCCESS!')
          debugLog('api', '🆔 File ID:', result.id || result.fileId)
          debugLog('api', '📦 Response:', result)
          debugGroupEnd('api')
          resolve({ success: true, fileId: result.id || result.fileId })
        } catch (error) {
          debugError('api', '❌ Response parsing error:', error)
          debugGroupEnd('api')
          resolve({ success: false, error: 'Ошибка парсинга ответа сервера' })
        }
      } else {
        let errorMessage = `HTTP ${xhr.status}: ${xhr.statusText}`
        try {
          const errorData = JSON.parse(xhr.responseText)
          if (errorData.message) {
            errorMessage = errorData.message
          }
          debugError('api', `❌ Upload FAILED: ${errorMessage}`)
          debugLog('api', '📦 Error Response:', errorData)
        } catch (e) {
          debugError('api', `❌ Upload FAILED: ${errorMessage}`)
        }
        debugGroupEnd('api')
        resolve({ success: false, error: errorMessage })
      }
    })
    
    // Обработка сетевых ошибок
    xhr.addEventListener('error', () => {
      debugError('api', '❌ Network error during upload')
      debugGroupEnd('api')
      resolve({ success: false, error: 'Ошибка сетевого соединения' })
    })
    
    // Обработка отмены
    xhr.addEventListener('abort', () => {
      debugLog('api', '⚠️ Upload aborted by user')
      debugGroupEnd('api')
      resolve({ success: false, error: 'Загрузка отменена' })
    })
    
    // Формируем URL с query параметрами
    const queryParams = new URLSearchParams()
    queryParams.append('group', groupId.toString())
    queryParams.append('name', name)
    queryParams.append('extension', extension)
    
    const url = `${API_BASE_URL}/ai/file?${queryParams.toString()}`
    
    debugLog('api', '🌐 URL:', url)
    debugLog('api', '🔑 Authorization:', `Bearer ${accessToken.substring(0, 20)}...`)
    debugLog('api', '📝 Content-Type:', 'application/octet-stream')
    debugLog('api', '🚀 Sending file as raw bytes...')
    
    // Открываем соединение и отправляем файл
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    
    // Отправляем файл как raw bytes
    xhr.send(file)
  })
}

/**
 * 🐛 УПРАВЛЕНИЕ ЛОГИРОВАНИЕМ
 * 
 * Теперь управляется через глобальный объект DEBUG:
 * 
 * 🔧 Основные команды:
 *   DEBUG.status()          - показать статус всех логов
 *   DEBUG.toggle("api")     - переключить API логи
 *   DEBUG.disableAll()      - отключить всё логирование
 *   DEBUG.enableAll()       - включить всё логирование
 *   DEBUG.help()            - показать полную справку
 * 
 * 💡 Старые команды работают для совместимости:
 *   window.toggleApiLogging()
 *   window.checkApiLogging()
 */ 